import {isEqual} from 'lodash';
import {useMemo} from 'react';
import {Project} from '@contentlab/types/project';
import {ProjectStatusUpdate} from '@contentlab/types/projectStatusUpdate';
import queryClient from '@/utils/reactQueryClient';
import projectFetchers from '@/api/fetchers/content-studio/project';
import useProjectsStatusQuery from '@/hooks/content-studio/use-projects-status-query';
import useReactQuery from '@/hooks/use-react-query';
import {CONTENT_LAB, PROJECTS} from '../consts';

export interface ProjectQueryParams {
  id: string | undefined | null;
  isDraft?: boolean;
  forceLive?: boolean;
  onError?: (err: Error) => void;
}

function useProjectQuery({
  id,
  isDraft = false,
  forceLive = false,
  onError,
}: ProjectQueryParams) {
  const queryKey = [CONTENT_LAB, PROJECTS, id];
  if (isDraft) {
    queryKey.push('draft');
  }
  const response = useReactQuery({
    queryKey,
    queryFn: id
      ? async (): Promise<Project> => projectFetchers.getProjectById(id)
      : undefined,
    staleTime: 1000 * 60,
    cacheTime: 1000 * 60,
    enabled: !!id,
    retry: false,
    onError,
  });

  const isProjectProcessing = useMemo(() => {
    return response.data?.status === 'in_progress';
  }, [response.data?.status]);

  // TODO: support passing ids in the backend
  useProjectsStatusQuery({
    enabled: isProjectProcessing || forceLive,
    updatedHoursAgo: 24,
    onChange: (reason, projectStatusUpdates: ProjectStatusUpdate[]) => {
      const {data: project} = response;
      if (!project) {
        return;
      }
      const projectStatus = projectStatusUpdates.find(
        (projectStatusUpdate) => projectStatusUpdate.projectId === project.id
      );
      if (!projectStatus) {
        return;
      }
      if (
        !isEqual(project.state, projectStatus.progressInfo) ||
        project.status !== projectStatus.status
      ) {
        queryClient.invalidateQueries({
          queryKey: [CONTENT_LAB, PROJECTS, id],
        });
      }
    },
  });

  return response;
}

export default useProjectQuery;
